import { IVenueCategory, INestedVenueCategory } from 'src/api/getVenueCategories/types';

export type CategoryBase = {
  category_id: string | null;
  parent_category_id: string | null;
  display_order: number;
};

export type Nested<T extends CategoryBase> = T & { children: Nested<T>[] };

const buildNestedRecursive = <T extends CategoryBase>(categories: T[], parentId: string | null = null): Nested<T>[] =>
  categories
    .filter((c) => c.parent_category_id === parentId)
    .sort((a, b) => a.display_order - b.display_order)
    .map((c) => ({ ...c, children: buildNestedRecursive(categories, c.category_id) }));

/**
 * Builds a nested category structure from a flat array of categories.
 * The uncategorised entry (category_id === null) is placed first with no children.
 */
export const buildNestedCategories = <T extends CategoryBase>(flatCategories: T[]): Nested<T>[] => {
  const uncategorised = flatCategories.filter((c) => c.category_id === null);
  const categories = flatCategories.filter((c) => c.category_id !== null);
  const nested = buildNestedRecursive(categories, null);
  return [...uncategorised.map((cat) => ({ ...cat, children: [] as Nested<T>[] })), ...nested];
};

/**
 * Builds a nested venue category structure, separating Uncategorised from the flat array.
 */
export const buildNestedVenueCategories = (flatCategories: IVenueCategory[]): INestedVenueCategory[] =>
  buildNestedCategories<IVenueCategory>(flatCategories);
